import { takeLatest, put, select, getContext } from 'redux-saga/effects';
import type { QueryClient } from 'react-query';
import { QUERY_KEY as USER_QUERY_KEY } from '@/hooks/useUser';
import {
  fetchUserProfile,
  updateUserProfile,
  fetchUserBilling,
  fetchUserAccess,
  hydrateUserProfile,
} from './routines';
import { isBoolean } from 'lodash';
import {
  getUser,
  updateUser,
  userOtp,
  getUacCheck,
} from '@simplywallst/services';
import type { Response as UserResponse } from '@simplywallst/services/lib/endpoints/getUser';
import { login, registration } from '@/redux/auth/routines';
import { addSnackBar } from '@components/SnackBar/redux';
import {
  getMarketCookie,
  getDefaultPortfolioCookie,
  getPortfolioHoldingsSortTermCookie,
  getPortfolioHoldingsHideLiquidatedCookie,
  canReadWriteCookies,
} from '@/utilities/cookies';
import { hasAccessTokenSelector } from '@/redux/auth/selectors';
import { ahoy, getAuthenticatedAhoy } from '@ducks/saga';
import { countries } from '@utilities/countries';
/** Sagas */

function* migrateCookieSettings({
  countryISO,
  defaultMarket,
  defaultPortfolio,
  holdingsSorting,
  showLiquidated,
}) {
  try {
    //  This condition is only needed for the sakes of migrating default cookie data from existing users to backend settings
    if (
      (defaultMarket === null ||
        defaultPortfolio === null ||
        holdingsSorting === null ||
        showLiquidated === null) &&
      canReadWriteCookies()
    ) {
      const settings = {
        defaultMarket: countryISO,
        holdingsSorting: '',
        showLiquidated: false,
      };
      if (defaultMarket === null) {
        const market = getMarketCookie();
        if (typeof market !== 'undefined' && market) {
          settings.defaultMarket = market;
        }
      }
      if (defaultPortfolio === null) {
        const portfolio = getDefaultPortfolioCookie();
        if (typeof portfolio !== 'undefined' && portfolio) {
          settings['defaultPortfolio'] = portfolio;
        }
      }
      if (holdingsSorting === null) {
        const sortTerm = getPortfolioHoldingsSortTermCookie();
        if (typeof sortTerm !== 'undefined' && sortTerm) {
          settings['holdingsSorting'] = sortTerm;
        }
      }
      if (showLiquidated === null) {
        const hideLiquidated = getPortfolioHoldingsHideLiquidatedCookie();
        if (isBoolean(hideLiquidated)) {
          settings.showLiquidated = !hideLiquidated; // reverse hideLiquidated
        }
      }
      console.log('migrateCookieSettings');
      yield put(updateUserProfile.trigger({ settings }));
    }
  } catch (e) {
    console.log('failed to migrateCookieSettings', e);
  }
}

export function* getOTPToken() {
  try {
    const caller = getAuthenticatedAhoy();
    return yield caller(userOtp);
  } catch (e) {
    return null;
  }
}

export function* fetchUserAccessWorker() {
  try {
    // Fetch UAC
    const hasToken: boolean = yield select(hasAccessTokenSelector);
    const caller = ahoy({
      authenticate: hasToken,
    });
    const response = yield caller(getUacCheck);
    const { data } = response;
    const responseData = data[0];
    yield put(
      fetchUserAccess.success({
        data: responseData,
      })
    );
  } catch (e: any) {
    console.log('fetchUserAccess.failure', e);
    return yield put(fetchUserAccess.failure(e.message));
  } finally {
    yield put(fetchUserAccess.fulfill());
  }
}

function* mapUserResponse(info: UserResponse) {
  const { logged_in: loggedIn } = info.data;
  const mutatedInfo = info;

  /**
   * This allows SSR pages to use cf-ipcountry header in detecting use
   * country. Only run on anonymous and SSR pages.
   */
  let cfIpCountry: undefined | string = yield select(
    (state) => state.ui.cfIpCountry
  );

  if (
    RUNTIME_ENV === 'server' &&
    loggedIn === false &&
    typeof cfIpCountry !== 'undefined'
  ) {
    cfIpCountry = cfIpCountry.toLowerCase();
    const matchedCountry = countries[cfIpCountry as keyof typeof countries];
    mutatedInfo.data.country_iso = cfIpCountry;
    mutatedInfo.data.currency = matchedCountry.currency;
    mutatedInfo.data.logged_in = false;
    mutatedInfo.data.settings = {
      data: {
        default_market: cfIpCountry,
        feed_sorting: null,
        show_liquidated: false,
        default_portfolio: null,
        holdings_sorting: null,
        has_seen_sa_modal: null,
        has_seen_subscription_modal: null,
        has_seen_trial_end_modal: null,
        has_seen_report_view_modal: null,
        stocks_view: null,
        flagr_entityId: null,
        default_watchlist_id: null,
        default_watchlist_sorting: null,
        default_watchlist_period: null,
        has_dismissed_wl_banner: null,
      },
    };
  } else if (loggedIn) {
    const {
      country_iso: countryISO,
      settings: {
        data: {
          default_market: defaultMarket,
          default_portfolio: defaultPortfolio,
          holdings_sorting: holdingsSorting,
          show_liquidated: showLiquidated,
        },
      },
    } = info.data;
    // This condition is only needed for the sakes of migrating default cookie data from existing users to backend settings
    yield migrateCookieSettings({
      countryISO,
      defaultMarket,
      defaultPortfolio,
      holdingsSorting,
      showLiquidated,
    });
  }

  return mutatedInfo;
}

function* runFetchUserBilling(info: UserResponse) {
  const billing = info?.data?.billing;
  if (billing) {
    yield put(fetchUserBilling.trigger());
    try {
      const billingData = {
        ...billing.data,
      };
      yield put(fetchUserBilling.success({ data: billingData }));
    } catch (e) {
      //trigger fetchUserBilling.success without prorated price if
      const billingData = {
        ...billing.data,
        proratedPlanPrice: 0,
      };
      yield put(fetchUserBilling.success({ data: billingData }));
      throw new Error('Failed to retrieve user billing data');
    }
  }
}

export function* fetchUserProfileWorker() {
  try {
    yield put(fetchUserProfile.request());
    const hasToken = yield select(hasAccessTokenSelector);
    const caller = ahoy({ authenticate: hasToken });
    const info: UserResponse | undefined = yield caller(getUser);

    if (info && info.data) {
      const mappedResponse = yield mapUserResponse(info);

      yield put(fetchUserProfile.success(mappedResponse));

      yield runFetchUserBilling(info);
    } else {
      throw new Error('Failed to retrieve user info');
    }
  } catch (e: any) {
    return yield put(fetchUserProfile.failure(e.message));
  } finally {
    yield put(fetchUserProfile.fulfill());
  }
}

function* hydrateUserProfileWorker(action) {
  try {
    const { payload } = action as ReturnType<
      typeof hydrateUserProfile['trigger']
    >;

    if (!payload) {
      throw new Error('Empty Payload');
    }

    const mappedResponse = yield mapUserResponse(payload);

    yield put(hydrateUserProfile.success(mappedResponse));

    yield runFetchUserBilling(payload);
  } catch (e: any) {
    yield put(fetchUserProfile.failure(e.message));
  }
}

function* updateUserProfileWorker(action) {
  try {
    if (!action.payload) yield put(updateUserProfile.failure('Empty Payload'));
    const { payload } = action as ReturnType<
      typeof updateUserProfile['trigger']
    >;
    const { settings } = payload;
    const data = {
      email: payload.email,
      given_name: payload.givenName,
      family_name: payload.familyName,
      password: payload.password,
      password_confirm: payload.passwordConfirm,
    };
    if (settings) {
      data['settings'] = {
        default_market: settings.defaultMarket,
        feed_sorting: settings.feedSorting,
        show_liquidated: settings.showLiquidated,
        holdings_sorting: settings.holdingsSorting,
        default_portfolio: settings.defaultPortfolio,
        has_seen_sa_modal: settings.hasSeenSAModal,
        has_seen_subscription_modal: settings.hasSeenSubscriptionModal,
        has_seen_trial_end_modal: settings.hasSeenTrialEndModal,
      };
    }
    const caller = getAuthenticatedAhoy();
    const response = yield caller(updateUser, data);

    if (response && response.data) {
      yield put(fetchUserProfile.success(response)); //Update the details
      yield put(updateUserProfile.success());
      const queryClient: QueryClient = yield getContext('queryClient');
      queryClient.refetchQueries([USER_QUERY_KEY]);
    } else {
      yield put(updateUserProfile.failure('No response data'));
    }
  } catch (err: any) {
    console.error('Failed to update user info..', action);
    yield put(updateUserProfile.failure(err.message));
  }
}

/** Watchers */

function* watchFetchUserAccess() {
  yield takeLatest(
    [
      fetchUserProfile.REQUEST,
      hydrateUserProfile.TRIGGER,
      login.SUCCESS,
      registration.SUCCESS,
    ],
    fetchUserAccessWorker
  );
}

function* watchFetchUserProfile() {
  yield takeLatest(
    [fetchUserProfile.TRIGGER, login.SUCCESS, registration.SUCCESS],
    fetchUserProfileWorker
  );
}

function* updateUserProfileSuccessSaga() {
  try {
    yield takeLatest(updateUserProfile.SUCCESS, function* () {
      yield put(
        addSnackBar.trigger({
          type: 'good',
          message: 'Settings Successfully Updated',
          lifeTime: 2000,
        })
      );
    });
  } catch (e) {
    console.log(e);
  }
}

function* updateUserProfileFailureSaga() {
  try {
    yield takeLatest(updateUserProfile.FAILURE, function* () {
      yield put(
        addSnackBar.trigger({
          type: 'bad',
          message: 'Failed to update Details',
          lifeTime: 4000,
        })
      );
    });
  } catch (e) {
    console.log(e);
  }
}

function* watchUpdateUserProfile() {
  yield takeLatest([updateUserProfile.TRIGGER], updateUserProfileWorker);
}

function* watchHydrateUserProfile() {
  yield takeLatest([hydrateUserProfile.TRIGGER], hydrateUserProfileWorker);
}

export const watchers = [
  watchFetchUserAccess,
  watchFetchUserProfile,
  updateUserProfileFailureSaga,
  updateUserProfileSuccessSaga,
  watchUpdateUserProfile,
  watchHydrateUserProfile,
];
